import React, { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Control, useWatch } from "react-hook-form";

import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { patchInvoiceHeader } from "@/features/invoices/api/invoices";
import {
  filterPaymentMethodsByCurrency,
  PAYMENT_METHOD_LABELS,
} from "@/utils/paymentMethods";

import { InvoiceFormValues } from "./types";

interface PaymentMethodSelectorProps {
  control: Control<InvoiceFormValues>;
  availablePaymentMethods: string[];
  disabled?: boolean;
  invoiceId?: string | null;
}

const PaymentMethodSelector = ({
  control,
  availablePaymentMethods,
  disabled,
  invoiceId,
}: PaymentMethodSelectorProps) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const dueDate = useWatch({
    control,
    name: "dueDate",
  });

  const { mutate: updatePaymentMethods } = useMutation({
    mutationFn: (paymentMethods: string[]) => {
      // Include the current due date in the API call
      const dueDateTimestamp = dueDate
        ? Math.floor(new Date(dueDate).getTime() / 1000)
        : undefined;

      return patchInvoiceHeader(invoiceId!, {
        paymentMethodTypes: paymentMethods,
        dueDate: dueDateTimestamp,
      });
    },
  });

  // Watch the currency field to filter payment methods
  const currency = useWatch({
    control,
    name: "currency",
    defaultValue: "USD",
  });

  // Watch the payment methods field
  const selectedPaymentMethods = useWatch({
    control,
    name: "paymentMethods",
    defaultValue: [],
  }) as string[];

  // Handle payment methods change
  const handlePaymentMethodsChange = React.useCallback(
    (methods: string[]) => {
      if (!invoiceId) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        updatePaymentMethods(methods);
      }, 300);
    },
    [invoiceId, updatePaymentMethods]
  );

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Filter payment methods based on the selected currency
  const filteredPaymentMethods = filterPaymentMethodsByCurrency(
    availablePaymentMethods,
    currency
  );

  // Update payment methods when currency changes
  useEffect(() => {
    if (selectedPaymentMethods.length > 0) {
      const validPaymentMethods = filterPaymentMethodsByCurrency(
        selectedPaymentMethods,
        currency
      );

      if (validPaymentMethods.length !== selectedPaymentMethods.length) {
        const form = control._formValues;
        if (form) {
          form.paymentMethods = validPaymentMethods;
          if (invoiceId) {
            handlePaymentMethodsChange(validPaymentMethods);
          }
        }
      }
    }
  }, [
    currency,
    selectedPaymentMethods,
    control,
    invoiceId,
    handlePaymentMethodsChange,
  ]);

  return (
    <FormField
      control={control}
      name="paymentMethods"
      render={({ field }) => (
        <FormItem className="space-y-2">
          <FormLabel>Choose Payment Methods</FormLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
            {filteredPaymentMethods.length > 0 ? (
              filteredPaymentMethods.map((method) => (
                <div key={method} className="flex items-center space-x-2">
                  <Checkbox
                    id={method}
                    checked={field.value?.includes(method)}
                    onCheckedChange={(checked) => {
                      let newValue;
                      if (checked) {
                        newValue = [...field.value, method];
                      } else {
                        newValue = field.value.filter(
                          (value) => value !== method
                        );
                      }
                      field.onChange(newValue);
                      handlePaymentMethodsChange(newValue);
                    }}
                    disabled={disabled}
                  />
                  <label
                    htmlFor={method}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {PAYMENT_METHOD_LABELS[method] || method}
                  </label>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-sm text-muted-foreground">
                No payment methods available for {currency}
              </div>
            )}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default PaymentMethodSelector;
